import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useUpdateEffect } from "react-use";
import { LayerContextProvider, layers } from "@activecollab/components";
import queryString from "query-string";
import { CalendarEventSheet } from "~/components/Sheet/CalendarEventSheet";
import { DiscussionSheet } from "~/components/Sheet/DiscussionSheet";
import { NoteSheet } from "~/components/Sheet/NoteSheet";
import { ObjectSheetProvider } from "~/components/Sheet/ObjectSheetContext";
import { TaskSheetWrapper } from "~/components/Sheet/TaskSheetWrapper";
import { PROJECT_TEMPLATE_NOTE } from "~/pages/ProjectTemplates/utils";
import { TemplateDiscussionAndNoteSheet } from "./TemplateDiscussionAndNoteSheet";
import { TemplateTaskSheet } from "./TemplateTaskSheet";

export const ObjectSheet = () => {
  const { search } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const sheetRef = useRef(null);
  const [disableFocusLock, setDisableFocusLock] = useState(false);

  const [state, setState] = useState({
    id: 0,
    secondArg: 0,
    thirdArg: undefined,
    entity: null,
    open: false,
  });

  useEffect(() => {
    const params = queryString.parse(search);
    const value = params?.modal || "";

    if (value.length > 0) {
      const result = value.split("-");
      let entity = null;
      const id = parseInt(result[1] || 0);
      const secondArg = parseInt(result[2] || 0);
      const thirdArg = parseInt(result[3] || undefined);
      let open = id > 0 && secondArg > 0;
      if (result[0]) {
        switch (result[0].toLowerCase()) {
          case "task":
            entity = "Task";
            break;
          case "discussion": {
            entity = "Discussion";
            break;
          }
          case "note": {
            entity = "Note";
            break;
          }
          case "calendarevent":
            entity = "CalendarEvent";
            break;
          case "templatetask":
            entity = "TemplateTask";
            break;
          case "templatenote":
            entity = "TemplateNote";
            break;
          case "templatediscussion":
            entity = "TemplateDiscussion";
            break;
        }
      }

      if (open) {
        setState({
          entity,
          secondArg,
          thirdArg,
          id,
          open,
        });
      }
    } else {
      setState((prevState) => {
        return {
          ...prevState,
          open: false,
        };
      });
    }
  }, [search]);

  useUpdateEffect(() => {
    if (!state.open && searchParams.has("modal")) {
      searchParams.delete("modal");
      if (searchParams.has("edit")) {
        searchParams.delete("edit");
      }
      setSearchParams(searchParams);
    }
  }, [state.open]);

  const onClose = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      open: false,
    }));
  }, []);

  const closeSheet = useCallback(() => {
    sheetRef.current?.close(); //this will trigger onClose after animation is finished
  }, []);

  return (
    <ObjectSheetProvider
      value={{
        open: state.open,
        closeSheet: closeSheet,
        disableFocusLock,
        setDisableFocusLock,
      }}
    >
      <LayerContextProvider value={{ zIndex: layers.house }}>
        {state.entity === "Task" && (
          <TaskSheetWrapper
            ref={sheetRef}
            id={state.id}
            projectId={state.secondArg}
            open={state.open}
            onClose={onClose}
          />
        )}
        {state.entity === "Discussion" && (
          <DiscussionSheet
            ref={sheetRef}
            id={state.id}
            projectId={state.secondArg}
            open={state.open}
            onClose={onClose}
          />
        )}
        {state.entity === "Note" && (
          <NoteSheet
            ref={sheetRef}
            id={state.id}
            projectId={state.secondArg}
            open={state.open}
            onClose={onClose}
          />
        )}
        {state.entity === "CalendarEvent" && (
          <CalendarEventSheet
            id={state.id}
            calendarId={state.secondArg}
            occurrence={state.thirdArg}
            ref={sheetRef}
            open={state.open}
            onClose={onClose}
          />
        )}
        {state.entity === "TemplateTask" && (
          <TemplateTaskSheet
            taskId={state.id}
            templateId={state.secondArg}
            ref={sheetRef}
            open={state.open}
            onClose={onClose}
          />
        )}
        {state.entity === "TemplateNote" && (
          <TemplateDiscussionAndNoteSheet
            elementId={state.id}
            templateId={state.secondArg}
            ref={sheetRef}
            open={state.open}
            onClose={onClose}
            type={PROJECT_TEMPLATE_NOTE}
          />
        )}
        {state.entity === "TemplateDiscussion" && (
          <TemplateDiscussionAndNoteSheet
            elementId={state.id}
            templateId={state.secondArg}
            ref={sheetRef}
            open={state.open}
            onClose={onClose}
          />
        )}
      </LayerContextProvider>
    </ObjectSheetProvider>
  );
};
